import { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { AxiosRequest } from "_services/api";
import { getIdUsuario } from "_services/auth";
import { toastErro } from "components/Toast";
import {
  ILoteEstampagem,
  IEstampagemPlacaItem,
  IValidarPlacaResponse,
  IValidarTagResponse,
  FiltrosLote,
  FiltrosItens,
  TipoLote,
} from "../interfaces/ILoteEstampagem";
import {
  buscarImpressoraPadrao,
  listarImpressoras,
  enviarZpl,
  ZebraDevice,
} from "_services/zebraBrowserPrint/zebraBrowserPrint.service";
import { gerarZplEtiquetaProcesso, gerarHtmlEtiqueta, DadosEtiquetaZpl } from "_services/zebraBrowserPrint/zplTemplate";

// const api = AxiosRequest.makeApi("http://localhost:6005/api/v2"); // TODO: retirar
const api = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
const apiDefault = AxiosRequest.makeApi();

const filtrosIniciais: FiltrosLote = {
  status: '',
};

// O banco armazena '' (string vazia) quando recebe 'LER_CODIGO_BARRAS' (ENUM inválido).
// Fazemos o mapeamento de volta no frontend.
function normalizarTipo(lote: ILoteEstampagem): ILoteEstampagem {
  return (lote.tipo as string) === '' ? { ...lote, tipo: 'LER_CODIGO_BARRAS' } : lote;
}

export function useLoteEstampagem() {
  const [lotes, setLotes] = useState<ILoteEstampagem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosLote>(filtrosIniciais);
  const [impressora, setImpressora] = useState<ZebraDevice | null>(null);
  const [erroImpressora, setErroImpressora] = useState<string | null>(null);
  const [impressoras, setImpressoras] = useState<ZebraDevice[]>([]);

  const verificarImpressora = useCallback(async () => {
    try {
      const device = await buscarImpressoraPadrao();
      setImpressora(device);
      setErroImpressora(null);
    } catch (error: any) {
      setImpressora(null);
      setErroImpressora(error?.message ?? "Impressora não encontrada");
    }
  }, []);

  const carregarImpressoras = useCallback(async () => {
    try {
      const lista = await listarImpressoras();
      setImpressoras(lista);
    } catch {
      setImpressoras([]);
    }
  }, []);

  const buscarLotes = useCallback(async (f?: FiltrosLote) => {
    setLoading(true);
    const params = f ?? filtros;
    try {
      const query = new URLSearchParams();
      if (params.status) { query.set("status", params.status); }
      if (params.tipo) { query.set("tipo", params.tipo); }
      if (params.idEmpresa) { query.set("idEmpresa", String(params.idEmpresa)); }
      if (params.dataInicio) { query.set("dataInicio", params.dataInicio); }
      if (params.dataFim) { query.set("dataFim", params.dataFim); }
      const { data } = await api.get<ILoteEstampagem[]>(`/lote-emplacamento?${query.toString()}`);
      setLotes(data.map(normalizarTipo));
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao buscar estampagens");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  const criarLote = useCallback(async (dados: { idEmpresa: number; nomeEmpresa: string; localEntrega?: string; comTag: boolean; tagJaRegistrada: boolean; tipo: TipoLote }): Promise<ILoteEstampagem> => {
    const idUsuario = getIdUsuario();
    const { data } = await api.post<ILoteEstampagem>("/lote-emplacamento", { ...dados, idUsuario: Number(idUsuario) });
    return data;
  }, []);

  const finalizarLote = useCallback(async (id: number): Promise<void> => {
    await api.patch(`/lote-emplacamento/${id}/fechar`);
    await buscarLotes();
  }, [buscarLotes]);

  const avancarStatus = useCallback(async (id: number, forcar = false): Promise<void> => {
    await api.patch(`/lote-emplacamento/${id}/avancar-status`, { forcar });
    await buscarLotes();
  }, [buscarLotes]);

  const abrirProcesso = useCallback(async (idProcesso: string): Promise<void> => {
    try {
      const { data } = await apiDefault.get(`/processos/param/${idProcesso}`);
      window.open(
        process.env.REACT_APP_DESPACHANTE_BASE_URL + "/pages/processo/Processo.php?param=" + data,
        "_blank",
      );
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao abrir processo");
    }
  }, []);

  const vincularAr = useCallback(async (id: number, ar: string): Promise<void> => {
    await api.patch(`/lote-emplacamento/${id}/vincular-ar`, { ar });
    await buscarLotes();
  }, [buscarLotes]);

  const buscarResumo = useCallback(async (id: number): Promise<{ lote: ILoteEstampagem; itens: IEstampagemPlacaItem[] }> => {
    const { data } = await api.get<{ lote: ILoteEstampagem; itens: IEstampagemPlacaItem[] }>(`/lote-emplacamento/${id}/resumo`);
    return data;
  }, []);

  const validarPlaca = useCallback(async (placa: string): Promise<IValidarPlacaResponse> => {
    const { data } = await api.get<IValidarPlacaResponse>(`/lote-emplacamento/validar-placa/${placa}`);
    return data;
  }, []);

  const validarTag = useCallback(async (tag: string): Promise<IValidarTagResponse> => {
    const { data } = await api.get<IValidarTagResponse>(`/lote-emplacamento/validar-tag/${tag}`);
    return data;
  }, []);

  const adicionarItem = useCallback(async (idLote: number, dados: { placa: string; idProcesso?: string; tag?: string }): Promise<IEstampagemPlacaItem> => {
    const idUsuario = getIdUsuario();
    const { data } = await api.post<IEstampagemPlacaItem>(`/lote-emplacamento/${idLote}/item`, { ...dados, idUsuario: Number(idUsuario) });
    return data;
  }, []);

  const listarItens = useCallback(async (idLote: number, filtrosItens?: Partial<FiltrosItens>): Promise<IEstampagemPlacaItem[]> => {
    const query = new URLSearchParams();
    if (filtrosItens?.placa) { query.set("placa", filtrosItens.placa); }
    if (filtrosItens?.ordenarPor) { query.set("ordenarPor", filtrosItens.ordenarPor); }
    if (filtrosItens?.ordenacao) { query.set("ordenacao", filtrosItens.ordenacao); }
    const { data } = await api.get<IEstampagemPlacaItem[]>(`/lote-emplacamento/${idLote}/item?${query.toString()}`);
    return data;
  }, []);

  const atualizarItem = useCallback(async (idLote: number, idItem: number, tag: string): Promise<void> => {
    await api.patch(`/lote-emplacamento/${idLote}/item/${idItem}`, { tag });
  }, []);

  const removerItem = useCallback(async (idLote: number, idItem: number): Promise<void> => {
    await api.delete(`/lote-emplacamento/${idLote}/item/${idItem}`);
  }, []);

  const exportarLoteDados = useCallback(async (id: number): Promise<any[]> => {
    const { data } = await api.get<any[]>(`/lote-emplacamento/${id}/exportar`);
    return data;
  }, []);

  const exportarLote = useCallback(async (id: number): Promise<void> => {
    const data = await exportarLoteDados(id);
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Estampagem');
    XLSX.writeFile(wb, `lote-estampagem-${id}.xlsx`);
  }, [exportarLoteDados]);

  const buscarTagPorPlaca = useCallback(async (placa: string): Promise<string | null> => {
    const { data } = await api.get<{ tag: string | null }>(`/lote-emplacamento/tag-por-placa/${placa}`);
    return data.tag;
  }, []);

  const lerPlaca = useCallback(async (imagemBase64: string): Promise<string> => {
    const { data } = await api.post<{ placa: string }>("/impressao-etiqueta/leitura-placa", { imagemBase64 });
    return data.placa;
  }, []);

  const buscarDadosEtiqueta = useCallback(async (placa: string): Promise<DadosEtiquetaZpl> => {
    const { data } = await api.get<DadosEtiquetaZpl>(`/impressao-etiqueta/etiqueta-por-placa/${placa}`);
    return data;
  }, []);

  const imprimirEtiqueta = useCallback(async (dados: DadosEtiquetaZpl): Promise<void> => {
    const zpl = gerarZplEtiquetaProcesso(dados);
    await enviarZpl(zpl, impressora ?? undefined);
  }, [impressora]);

  const imprimirEtiquetaPdf = useCallback(async (dados: DadosEtiquetaZpl): Promise<void> => {
    const html = await gerarHtmlEtiqueta(dados);
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  }, []);

  const atualizarStatusImpressao = useCallback(async (idProcesso: string, status: string, mensagemErro?: string): Promise<void> => {
    await api.patch(`/impressao-etiqueta/${idProcesso}/status`, { status, mensagemErro });
  }, []);

  const buscarEventosCorreios = useCallback(async (ar: string): Promise<string[][]> => {
    const arNormalizado = ar.toUpperCase().endsWith("BR") ? ar : `${ar}BR`;
    const { data } = await apiDefault.get<string[][]>(`/processos/rastreio/correio/${arNormalizado}`);
    return Array.isArray(data) ? data : [];
  }, []);

  const verificarEntregaCorreios = useCallback(async (id: number, ar: string): Promise<boolean> => {
    const eventos = await buscarEventosCorreios(ar);
    const entregue = eventos.some((row) =>
      row.some((celula) => typeof celula === 'string' && celula.toUpperCase().includes('ENTREGUE'))
    );
    if (entregue) {
      await avancarStatus(id);
    }
    return entregue;
  }, [buscarEventosCorreios, avancarStatus]);

  useEffect(() => {
    buscarLotes();
  }, [buscarLotes]);

  return {
    lotes,
    loading,
    filtros,
    setFiltros,
    impressora,
    setImpressora,
    impressoras,
    erroImpressora,
    carregarImpressoras,
    buscarLotes,
    criarLote,
    finalizarLote,
    avancarStatus,
    buscarResumo,
    validarPlaca,
    validarTag,
    adicionarItem,
    listarItens,
    atualizarItem,
    removerItem,
    exportarLoteDados,
    exportarLote,
    abrirProcesso,
    vincularAr,
    buscarTagPorPlaca,
    lerPlaca,
    buscarDadosEtiqueta,
    imprimirEtiqueta,
    imprimirEtiquetaPdf,
    atualizarStatusImpressao,
    verificarImpressora,
    buscarEventosCorreios,
    verificarEntregaCorreios,
  };
}
